import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {Table, mapTableColumns} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/documents/checks/grid/table';
import {ChecksGridFormattedRow, ChecksGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (data: ChecksGridRawRow[] | undefined, t: TFunction) => ChecksGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
}

export const ChecksGridTable = ({configFunc, dataMapper, exportConfig, headerControls}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t],
    );
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);

    return (
        <>
            <Gate />
            <Table
                columns={columns}
                data={mappedData}
                hasNext={data?.hasNext}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                userConfig={{
                    gridName: 'checksGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                headerControls={headerControls}
                exportConfig={exportConfig}
            />
        </>
    );
};
